import { canFactionRespawn, shrineOf } from '../arena.ts';
import { CLASSES } from '../classes.ts';
import {
  BODY_RADIUS,
  CALL_DISTANCE,
  CALL_RANGE,
  type Element,
  FATIGUE_MAX,
  RAISE_FRACTIONS,
  RESURRECT_RANGE,
} from '../constants.ts';
import { markWarp } from '../inputGuard.ts';
import { dist, dist2d, forwardVector, v3, type Vec3 } from '../math.ts';
import { applyDamage, awardXp, isEmbodied, type PlayerState } from '../player.ts';
import { levelForXp, tierForSpell, valhallaPenalty, XP_AWARDS } from '../progression.ts';
import { GLOBAL_COOLDOWN, SPELLS_BY_ID } from '../spells.ts';
import type { SimEvent, SimState } from './state.ts';

/**
 * Damage, death and the ways back: Call Ghost, Resurrect, and the walk through
 * Valhalla.
 *
 * `dealDamage` is the one door every hit goes through — friendly fire, XP,
 * kill credit and the death itself are all decided here, so a new kind of spell
 * cannot quietly skip any of them.
 */

export function dealDamage(
  s: SimState, source: PlayerState | null, target: PlayerState,
  amount: number, element: Element, out: SimEvent[],
): void {
  // No friendly fire.
  if (source && source.faction === target.faction) return;

  const res = applyDamage(target, amount, element);
  if (res.applied === 0) return;

  out.push({
    type: 'hit', targetId: target.id, sourceId: source?.id ?? '', amount: res.applied, element,
  });

  if (source) {
    source.damageDealt += res.applied;
    if (awardXp(source, res.applied * XP_AWARDS.perDamageDealt * s.xpScale)) {
      out.push({ type: 'level', playerId: source.id, level: source.level });
    }
  }

  if (res.killed) {
    if (source) {
      source.kills += 1;
      s.match.scores[source.faction].kills += 1;
      if (awardXp(source, XP_AWARDS.killBonus * s.xpScale)) {
        out.push({ type: 'level', playerId: source.id, level: source.level });
      }
    }
    killPlayer(target, source?.id ?? null, out);
  }
}

export function killPlayer(p: PlayerState, killerId: string | null, out: SimEvent[]): void {
  p.alive = false;
  p.ghost = true;
  p.flying = false;
  p.deaths += 1;
  p.effects.length = 0;
  p.ley = 0;
  p.moveScale = 1;
  p.pendingImpulse = null;
  // A ghost never reaches the casting tick, so a wind-up left set here would sit
  // waiting and finish itself the instant its owner stood back up.
  p.casting = null;
  out.push({ type: 'death', playerId: p.id, killerId });
}

/**
 * Puts a mage back on their feet.
 *
 * `fraction` applies to all three bars rather than only to health. A mage called
 * back mid-fight with a full power bar is a mage who never really died, and it
 * would make a Cleric standing behind the line able to simply undo the fight.
 */
export function reviveAt(p: PlayerState, pos: Vec3, fraction: number): void {
  p.pos = { ...pos };
  // Legitimately relocated, so the movement guard stands down for a moment.
  markWarp(p);
  p.alive = true;
  p.ghost = false;
  p.inValhalla = false;
  p.vel = v3();
  p.verticalVelocity = 0;
  p.moveScale = 1;
  p.pendingImpulse = null;
  p.casting = null;
  p.hp = Math.max(1, Math.round(p.hpMax * fraction));
  p.pp = Math.round(p.ppMax * fraction);
  p.fatigue = FATIGUE_MAX * fraction;
  p.ppAccumulator = 0;
  p.hpAccumulator = 0;
  p.poolChargeAge = Infinity;
}

/** Cleric innate. Draws the nearest fallen teammate to you and raises them. */
/**
 * Call Dead. It calls, and then it raises.
 *
 * The ghost is pulled to the Cleric from anywhere within earshot rather than
 * the Cleric having to walk to the body — which is what the name always
 * promised and what makes a Cleric worth guarding. A ghost cannot refuse; being
 * called is better than the walk to Valhalla, and giving it a choice would mean
 * a mechanic nobody in a hurry would ever use.
 */
/**
 * Call Ghost. Pulls the nearest fallen ally on this side to the Cleric's feet.
 *
 * They arrive **still a ghost**. Raising them is a second act, which is the
 * whole point of the split: a Cleric can gather several before deciding who to
 * spend the power on, and pulling somebody out of a fire is worth doing even
 * when there is nothing left to raise them with.
 */
export function tryCallGhost(s: SimState, cleric: PlayerState, out: SimEvent[]): void {
  if (CLASSES[cleric.cls].id !== 'cleric') return;
  if (!isEmbodied(cleric)) return;

  const spell = SPELLS_BY_ID.call_ghost;
  const tier = tierForSpell(spell, cleric.level);
  if (tier <= 0) return;
  if (cleric.callCooldown > 0 || cleric.gcd > 0) return;

  const cost = spell.ppCost[tier - 1];
  if (cleric.pp < cost || cleric.fatigue < spell.fatigueCost) return;

  let nearest: PlayerState | null = null;
  let best = Infinity;
  for (const other of s.players) {
    if (other.id === cleric.id) continue;
    if (other.faction !== cleric.faction) continue;
    if (!other.ghost || other.inValhalla) continue;
    const d = dist(cleric.pos, other.pos);
    if (d < best && d <= CALL_RANGE) {
      best = d;
      nearest = other;
    }
  }
  // A whiff, and deliberately a cheap one: nobody to call costs nothing, so a
  // Cleric hammering the key in a panic is not punished for it.
  if (!nearest) return;

  const dir = forwardVector(cleric.yaw, 0);
  nearest.pos = {
    x: cleric.pos.x + dir.x * CALL_DISTANCE,
    y: cleric.pos.y,
    z: cleric.pos.z + dir.z * CALL_DISTANCE,
  };
  markWarp(nearest);
  nearest.vel = v3();
  nearest.verticalVelocity = 0;

  cleric.pp -= cost;
  cleric.fatigue = Math.max(0, cleric.fatigue - spell.fatigueCost);
  cleric.callCooldown = spell.cooldown;
  cleric.gcd = GLOBAL_COOLDOWN;
  out.push({ type: 'cast', playerId: cleric.id, spellId: spell.id, tier });
  out.push({ type: 'called', playerId: nearest.id, byId: cleric.id });
}

/**
 * Resurrect. Brings a ghost at the Cleric's feet back to life.
 *
 * Short range on purpose: you have just pulled them here, and a Cleric who could
 * raise across a room would never have to leave one.
 */
export function tryResurrect(s: SimState, cleric: PlayerState, out: SimEvent[]): void {
  if (CLASSES[cleric.cls].id !== 'cleric') return;
  if (!isEmbodied(cleric)) return;

  const spell = SPELLS_BY_ID.resurrect;
  const tier = tierForSpell(spell, cleric.level);
  if (tier <= 0) return;
  if (cleric.raiseCooldown > 0 || cleric.gcd > 0) return;

  const cost = spell.ppCost[tier - 1];
  if (cleric.pp < cost) return;

  let nearest: PlayerState | null = null;
  let best = Infinity;
  for (const other of s.players) {
    if (other.id === cleric.id) continue;
    if (other.faction !== cleric.faction) continue;
    if (!other.ghost || other.inValhalla) continue;
    const d = dist(cleric.pos, other.pos);
    if (d < best && d <= RESURRECT_RANGE) {
      best = d;
      nearest = other;
    }
  }
  if (!nearest) return;

  const dir = forwardVector(cleric.yaw, 0);
  reviveAt(nearest, {
    x: cleric.pos.x + dir.x * 1.8,
    y: cleric.pos.y,
    z: cleric.pos.z + dir.z * 1.8,
  }, RAISE_FRACTIONS[tier - 1]);

  cleric.pp -= cost;
  cleric.fatigue = Math.max(0, cleric.fatigue - spell.fatigueCost);
  cleric.raiseCooldown = spell.cooldown;
  cleric.gcd = GLOBAL_COOLDOWN;

  if (awardXp(cleric, XP_AWARDS.raiseBonus * s.xpScale)) {
    out.push({ type: 'level', playerId: cleric.id, level: cleric.level });
  }
  out.push({ type: 'cast', playerId: cleric.id, spellId: spell.id, tier });
  out.push({ type: 'raised', playerId: nearest.id, byId: cleric.id });
}

/**
 * The shrine route back. Faster than waiting for a Cleric, but it drops you in
 * Valhalla — a dead zone you have to walk out of — and burns a quarter of the
 * experience you have earned this match.
 */
export function trySelfResurrect(s: SimState, p: PlayerState, out: SimEvent[]): void {
  if (!p.ghost || p.inValhalla) return;
  if (!canFactionRespawn(s.arena, p.faction)) return;

  // A third of the progress made since the last level, so the walk can never
  // cost one. See valhallaPenalty.
  const penalty = valhallaPenalty(p.xp);
  p.xp = Math.max(0, p.xp - penalty);
  p.matchXp = Math.max(0, p.matchXp - penalty);
  p.level = levelForXp(p.xp);

  const zone = s.arena.valhalla[0];
  if (zone) {
    /*
     * Arrivals queue along the corridor rather than ringing the pad.
     *
     * Everybody used to materialise on `zone.pos` exactly, which one at a time
     * is fine and several at once is a pile of capsules sharing one centre — and
     * a character controller cannot resolve that, so they hold each other still.
     * Ringing them fixed the pile and replaced it with a tight circle: six mages
     * on a one-metre radius are a body width apart with twenty centimetres
     * between them, close enough to interlock the moment anybody pushes, and
     * close enough that somebody arriving into the middle of it is walled in.
     *
     * A ring was the wrong shape for the space. Valhalla is a *corridor* — fifty
     * metres of it, twelve wide, with the way out at one end — and everybody in
     * it is walking the same direction. So arrivals are spaced along that walk,
     * a couple of metres apart, with a slight stagger across it: a queue, which
     * is what a line of people heading for the same door is, and each one starts
     * a little further along than the last rather than on top of them.
     */
    const already = s.players.reduce(
      (n, other) => (other.id !== p.id && other.inValhalla ? n + 1 : n), 0,
    );
    const out2 = zone.exits[0];
    const along = out2
      ? { x: out2.x - zone.pos.x, z: out2.z - zone.pos.z }
      : { x: 0, z: 1 };
    const span = Math.hypot(along.x, along.z) || 1;
    const dir = { x: along.x / span, z: along.z / span };
    // Across the corridor, which is the walk turned ninety degrees.
    const side = { x: -dir.z, z: dir.x };

    // Never more than a third of the way down: a queue that reaches the exit is
    // a queue that lets people out without walking, which is not the point.
    const forward = Math.min(already * 2.2, span / 3);
    const stagger = (already % 3 - 1) * BODY_RADIUS * 3;
    const at = {
      x: zone.pos.x + dir.x * forward + side.x * stagger,
      y: zone.pos.y,
      z: zone.pos.z + dir.z * forward + side.z * stagger,
    };

    reviveAt(p, at, 1);
    p.inValhalla = true;
    out.push({ type: 'valhalla', playerId: p.id });
  }
}

/** Walking into the teleporter at the end of Valhalla puts you back in the fight. */
export function checkValhallaExit(s: SimState, p: PlayerState, out: SimEvent[]): void {
  if (!p.inValhalla) return;
  for (const zone of s.arena.valhalla) {
    for (const exit of zone.exits) {
      if (dist2d(p.pos, exit) < 2.5) {
        const shrine = shrineOf(s.arena, p.faction);
        const spawns = s.arena.spawns[p.faction];
        const target = shrine && !shrine.conquered
          ? shrine.respawn
          : spawns[s.rng.int(0, Math.max(0, spawns.length - 1))];
        reviveAt(p, target, 1);
        out.push({ type: 'respawn', playerId: p.id });
        return;
      }
    }
  }
}
